import * as Tone from 'tone';

/**
 * Render a loop segment into its own AudioBuffer with a seamless wrap.
 *
 * Instead of fading the edges to silence (which puts an audible dip at every
 * wrap), the tail is crossfaded into the audio that naturally leads into the
 * loop start, so the wrap point is literally continuous — the last samples
 * become the real lead-in to the first ones.
 *
 * The crossfade is linear, which makes it an exact no-op when the lead-in IS
 * the tail (a whole-file loop that's already perfect loops bit-exact), and a
 * clickless blend for arbitrary cut points. The head is never touched, so a
 * downbeat at position 0 stays intact.
 */
export function createLoopBuffer(buffer, start, end, fadeSec = 0.01) {
  const sr = buffer.sampleRate;
  const s = Math.max(0, Math.floor((start || 0) * sr));
  const e = Math.min(buffer.length, Math.floor((end || buffer.duration) * sr));
  const len = Math.max(1, e - s);
  const out = Tone.context.createBuffer(buffer.numberOfChannels, len, sr);
  const fade = Math.min(Math.floor(fadeSec * sr), Math.floor(len / 4));

  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const src = buffer.getChannelData(ch);
    const dst = out.getChannelData(ch);
    for (let i = 0; i < len; i++) dst[i] = src[s + i];

    for (let i = 0; i < fade; i++) {
      const w = (i + 1) / fade;
      // Lead-in to the loop start: the audio just before `s`, or — when the
      // segment starts at the top of the file — the segment's own tail
      // (loop-land wraparound, which reduces the blend to identity).
      const leadIdx = s - fade + i >= 0 ? s - fade + i : e - fade + i;
      const j = len - fade + i;
      dst[j] = dst[j] * (1 - w) + src[leadIdx] * w;
    }
  }
  return out;
}
